import { autoinject, bindable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { StatusDotType } from 'elements/status-dot';
import { AsyncErrorHandler } from 'lib/ui';
import { CustomerService } from 'services/customer-service';
import { SiteService } from 'services/site-service';

@autoinject()
export class AquacomStatus {
  @bindable()
  siteId: number;
  siteIdChanged(newValue: number) {
    if (!newValue) return;
    void this.init();
  }

  @bindable()
  customerId: number;
  customerIdChanged(newValue: number) {
    if (!newValue) return;
    void this.init();
  }

  @bindable()
  // 3 = OK, 2 = Failed, 1 = Not transferred
  syncStatus?: number;

  @bindable()
  syncedAt?: Date;

  protected customerStatusText = '';
  protected transferStatusText = '';

  protected status: {
    customer: StatusDotType;
    customerText: string;

    showSite: boolean;
    site: StatusDotType;
    siteText: string;

    showTransfer: boolean;
    transferred: StatusDotType;
    transferredText: string;
  } = {
    customer: 'error',
    customerText: 'general.missingCustomerInAquacom',

    showSite: false,
    site: 'error',
    siteText: '',

    showTransfer: false,
    transferred: 'stale',
    transferredText: '',
  };

  private inProgress = false;

  constructor(
    private t: I18N,
    private customerService: CustomerService,
    private siteService: SiteService
  ) {}

  async init() {
    if (this.inProgress) return;

    this.inProgress = true;
    await this.getCustomerStatus();
    await this.getSiteStatus();

    this.inProgress = false;
  }

  @AsyncErrorHandler
  protected async getCustomerStatus() {
    if (!this.customerId) return;
    const { data } = await this.customerService.getCustomerStatus(this.customerId);

    if (!data.IsAquacomCustomer) {
      return;
    }

    const status: typeof this.status = {
      customer: 'error',
      customerText: '',
      showSite: false,
      site: 'stale',
      siteText: '',
      showTransfer: true,
      ...this.getTransferStatus(),
    };

    if (data.IsCustomerActive) {
      status.customer = 'ok';
      status.customerText = 'general.customerActiveInAquacom';
    } else if (!data.IsCustomerActive) {
      status.customer = 'warning';
      status.customerText = 'general.customerNotActiveInAquacom';
    }

    this.status = status;
  }

  private getTransferStatus(): { transferred: StatusDotType; transferredText: string } {
    if (this.syncStatus === 3) {
      return { transferred: 'ok', transferredText: this.t.tr('general.transferedTo', { x: 'Aquacom' }) };
    }

    if (this.syncStatus === 2) {
      return { transferred: 'error', transferredText: this.t.tr('general.transferedFailedTo', { x: 'Aquacom' }) };
    }

    return { transferred: 'stale', transferredText: this.t.tr('general.notTransferedTo', { x: 'Aquacom' }) };
  }

  @AsyncErrorHandler
  protected async getSiteStatus() {
    if (!this.siteId) return;
    const { data } = await this.siteService.getSiteStatus(this.siteId);

    if (!data.IsAquacomCustomer) {
      return;
    }

    const status: typeof this.status = {
      customer: 'error',
      customerText: '',
      showSite: true,
      site: 'stale',
      siteText: '',
      showTransfer: true,
      ...this.getTransferStatus(),
    };

    if (!data.CustomerFound) {
      status.customer = 'error';
      status.customerText = 'general.missingCustomerInAquacom';
    } else if (!data.IsCustomerActive) {
      status.customer = 'warning';
      status.customerText = 'general.customerNotActiveInAquacom';
    } else {
      status.customer = 'ok';
      status.customerText = 'general.customerActiveInAquacom';
    }

    if (!data.SiteFound) {
      status.site = 'error';
      status.siteText = 'general.missingSiteInAquacom';
    } else if (!data.IsSiteActive) {
      status.site = 'warning';
      status.siteText = 'general.siteNotActiveInAquacom';
    } else {
      status.site = 'ok';
      status.siteText = 'general.customerActiveInAquacom';
    }

    this.status = status;
  }
}
